/**
 * Time utilities — toate timestamp-urile se afiseaza in Romania (EET/EEST).
 *
 * DB-ul stocheaza in UTC (Postgres timestamptz). Frontend-ul face conversia
 * la afisare, folosind timezone-ul Europe/Bucharest.
 *
 * Note: EET = UTC+2 (iarna), EEST = UTC+3 (vara, DST). `Intl.DateTimeFormat`
 * cu timeZone:'Europe/Bucharest' face conversia corect automat (inclusiv DST).
 */

const RO_TZ = 'Europe/Bucharest';
const RO_LOCALE = 'ro-RO';

interface FormatOpts {
  /** Include ora (default: true). */
  withTime?: boolean;
  /** Format lung sau scurt (default: medium). */
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
}

const DEFAULT_OPTS: Required<FormatOpts> = {
  withTime: true,
  dateStyle: 'medium',
};

/**
 * Formateaza un timestamp (UTC din DB) in format romanesc local.
 *
 * Exemplu:
 *   formatRoTime('2026-09-03T13:30:00+00:00')
 *   // -> "3 sept. 2026, 16:30" (vara, UTC+3)
 *   // -> "3 sept. 2026, 15:30" (iarna, UTC+2)
 */
export function formatRoTime(input: string | number | Date | null | undefined, opts: FormatOpts = {}): string {
  if (input == null) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  const o = { ...DEFAULT_OPTS, ...opts };

  try {
    return new Intl.DateTimeFormat(RO_LOCALE, {
      timeZone: RO_TZ,
      dateStyle: o.dateStyle,
      ...(o.withTime && { timeStyle: 'short' }),
    }).format(d);
  } catch {
    // fallback daca Intl esueaza (e.g. browser foarte vechi)
    return d.toISOString();
  }
}

/**
 * Doar ora, fara data. Util pt "postat acum 5 min" sau comment timestamps.
 *
 *   formatRoHour('2026-09-03T13:30:00+00:00')
 *   // -> "16:30"
 */
export function formatRoHour(input: string | number | Date | null | undefined): string {
  if (input == null) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(RO_LOCALE, {
      timeZone: RO_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toISOString().slice(11, 16);
  }
}

/**
 * "acum 5 minute", "acum 2 ore", "acum 3 zile".
 * Pentru UI-ul de feed (chat, comments, photo feed).
 */
export function formatRoRelative(input: string | number | Date | null | undefined): string {
  if (input == null) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  try {
    if (diffSec < 30) return 'chiar acum';
    if (diffSec < 60) return `acum ${diffSec} secunde`;
    if (diffMin === 1) return 'acum 1 minut';
    if (diffMin < 60) return `acum ${diffMin} minute`;
    if (diffHour === 1) return 'acum 1 oră';
    if (diffHour < 24) return `acum ${diffHour} ore`;
    if (diffDay === 1) return 'ieri';
    if (diffDay < 7) return `acum ${diffDay} zile`;
    // peste 7 zile, afisam data completa
    return formatRoTime(d, { withTime: false, dateStyle: 'medium' });
  } catch {
    return d.toISOString();
  }
}

/**
 * Format scurt pt liste dense: "03.09.2026 16:30"
 */
export function formatRoShort(input: string | number | Date | null | undefined): string {
  if (input == null) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(RO_LOCALE, {
      timeZone: RO_TZ,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toISOString();
  }
}
